import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

const AuthContext = createContext({
  token: null,
  user: null,
  isAuthenticated: false,
  isReady: false,
  login: (_token, _user) => {},
  logout: () => {},
});

const readStoredToken = () => {
  try {
    return typeof window !== "undefined" ? localStorage.getItem("token") : null;
  } catch {
    return null;
  }
};

const readStoredUser = () => {
  try {
    if (typeof window === "undefined") {
      return null;
    }
    const raw = localStorage.getItem("user");
    if (!raw) {
      return null;
    }
    try {
      return JSON.parse(raw);
    } catch {
      return raw;
    }
  } catch {
    return null;
  }
};

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(() => readStoredToken());
  const [user, setUser] = useState(() => readStoredUser());
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    try {
      setToken(readStoredToken());
      setUser(readStoredUser());
    } catch {
      setToken(null);
      setUser(null);
    } finally {
      setIsReady(true);
    }

    const onStorage = (e) => {
      if (e.key === "token" || e.key === "user") {
        try {
          setToken(readStoredToken());
          setUser(readStoredUser());
        } catch {}
      }
    };

    if (typeof window !== "undefined") {
      window.addEventListener("storage", onStorage);
      return () => window.removeEventListener("storage", onStorage);
    }

    return undefined;
  }, []);

  const login = (newToken, newUser) => {
    try {
      localStorage.setItem("token", newToken);
      if (newUser !== undefined) {
        const toStore = typeof newUser === "string" ? newUser : JSON.stringify(newUser);
        localStorage.setItem("user", toStore);
      }
    } catch {}
    setToken(newToken);
    setUser(newUser ?? null);
  };

  const logout = () => {
    try {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
    } catch {}
    setToken(null);
    setUser(null);
  };

  const value = useMemo(() => ({
    token,
    user,
    isAuthenticated: !!token,
    isReady,
    login,
    logout,
  }), [token, user, isReady]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
